/**
 * API service for backend communication
 * Integrates with existing tokenManager from lib/api
 */

// Use empty string in dev (lets Vite proxy handle it), or explicit URL in production
const API_BASE_URL = import.meta.env.VITE_API_URL || '';

interface ApiError {
  error: string;
  // DRF error bodies use `detail` (e.g. 404 "Not found.", 403 "…") rather than
  // our custom `error` key — fall back to it so those surface a real message.
  detail?: string;
  details?: any;
}

// Use existing token manager
const TOKEN_KEY = 'access_token';

export class ApiClient {
  private baseUrl: string;
  private cache = new Map<string, { data: unknown; expiresAt: number }>();
  private static CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  /** Returns true for URLs eligible for in-memory caching. */
  private isCacheable(url: string): boolean {
    return url.includes('/api/govdata/');
  }

  /** Clear all cached govdata responses. */
  clearCache(): void {
    this.cache.clear();
  }

  private getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const headers: Record<string, string> = {
      ...(options.headers as Record<string, string>),
    };

    // Add auth token if available and not already set
    const token = this.getToken();
    if (token && !headers['Authorization']) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Only add Content-Type for non-FormData requests
    if (!(options.body instanceof FormData) && !headers['Content-Type']) {
      headers['Content-Type'] = 'application/json';
    }

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers,
      cache: 'no-store',
    });

    if (!response.ok) {
      const error: ApiError = await response.json().catch(() => ({
        error: `HTTP ${response.status}: ${response.statusText}`,
      }));
      // Include details in error message if available
      const errorMessage = error.error || error.detail || 'Request failed';
      const fullError = error.details
        ? `${errorMessage}: ${JSON.stringify(error.details)}`
        : errorMessage;
      throw new Error(fullError);
    }

    // 204 No Content (and other empty-body successes, e.g. DELETE) have nothing
    // to parse — calling response.json() on them throws "Unexpected end of JSON
    // input". Return undefined instead so callers like deleteStep/deleteEdge
    // resolve cleanly and run their post-success refresh.
    if (response.status === 204) {
      return undefined as T;
    }
    const text = await response.text();
    return (text ? JSON.parse(text) : undefined) as T;
  }

  async get<T>(endpoint: string, params?: Record<string, any>): Promise<T> {
    let url = endpoint;
    if (params) {
      const queryString = new URLSearchParams(
        Object.entries(params)
          .filter(([_, value]) => value !== undefined && value !== null)
          .map(([key, value]) => [key, String(value)])
      ).toString();
      if (queryString) {
        url = `${endpoint}?${queryString}`;
      }
    }
    const response = await this.request<any>(url, { method: 'GET' });
    
    // Handle paginated responses from DRF
    // If the response has a 'results' key, it's paginated - extract the results array
    if (response && typeof response === 'object' && 'results' in response) {
      return response.results as T;
    }
    
    return response as T;
  }

  async post<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data instanceof FormData ? data : JSON.stringify(data),
    });
  }

  async put<T>(endpoint: string, data: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async patch<T>(endpoint: string, data: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  /**
   * GET request that returns the raw response body without unwrapping `results`.
   * Use this for endpoints that return custom envelopes (e.g., govdata endpoints
   * that return { results, filters }).
   *
   * Govdata responses are cached in-memory (24h TTL) to avoid redundant round
   * trips when navigating between tabs or drilling down and back.
   */
  async getRaw<T>(endpoint: string, params?: Record<string, any>): Promise<T> {
    let url = endpoint;
    if (params) {
      const queryString = new URLSearchParams(
        Object.entries(params)
          .filter(([_, value]) => value !== undefined && value !== null)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([key, value]) => [key, String(value)])
      ).toString();
      if (queryString) {
        url = `${endpoint}?${queryString}`;
      }
    }

    // Check in-memory cache for govdata endpoints
    if (this.isCacheable(url)) {
      const cached = this.cache.get(url);
      if (cached && Date.now() < cached.expiresAt) {
        return cached.data as T;
      }
    }

    const result = await this.request<T>(url, { method: 'GET' });

    // Store in cache for govdata endpoints
    if (this.isCacheable(url)) {
      this.cache.set(url, {
        data: result,
        expiresAt: Date.now() + ApiClient.CACHE_TTL_MS,
      });
      // Lazy eviction of expired entries when cache grows large
      if (this.cache.size > 200) {
        const now = Date.now();
        for (const [key, entry] of this.cache) {
          if (now >= entry.expiresAt) this.cache.delete(key);
        }
      }
    }

    return result;
  }

  // ===========================================
  // MarketView API Methods
  // ===========================================

  async marketviewSearch(params: {
    query?: string
    scorecard_id?: number
    technology_ids?: number[]
    discipline_ids?: number[]
    limit?: number
    sort_by?: string
    program_id?: number
    solution_family_id?: string
  }): Promise<MarketViewSearchResponse> {
    return this.post<MarketViewSearchResponse>(
      '/api/vendors/marketview/search/',
      params
    )
  }

  async marketviewFetchPage(params: {
    search_id: string
    offset: number
    page_size?: number
  }): Promise<MarketViewSearchResponse> {
    const qs = new URLSearchParams({
      search_id: params.search_id,
      offset: String(params.offset),
      ...(params.page_size ? { page_size: String(params.page_size) } : {}),
    })
    return this.get<MarketViewSearchResponse>(
      `/api/vendors/marketview/search/page/?${qs.toString()}`
    )
  }

  async marketviewGetSpecs(technologyIds: number[]): Promise<MarketViewSpecsResponse> {
    return this.get<MarketViewSpecsResponse>(
      `/api/vendors/marketview/specs/?technology_ids=${technologyIds.join(',')}`
    )
  }

  async marketviewRescore(params: {
    product_ids: number[]
    spec_criteria?: Record<string, any>
    sort_by?: string
  }): Promise<MarketViewRescoreResponse> {
    return this.post<MarketViewRescoreResponse>(
      '/api/vendors/marketview/rescore/',
      params
    )
  }

  async marketviewGetDisciplines(): Promise<MarketViewDisciplinesResponse> {
    return this.get<MarketViewDisciplinesResponse>(
      '/api/vendors/marketview/disciplines/'
    )
  }
}

// ===========================================
// MarketView Types
// ===========================================

export interface ProductImage {
  id: number
  image_url: string
  source_url: string
  is_primary: boolean
  order: number
  width: number | null
  height: number | null
  file_size: number | null
  image_format: string
  created_at: string
}

export interface AgencyRelationshipSummary {
  agency_name: string
  sub_agency_name: string
  relationship_type: string
  relationship_strength: string
}

export interface MarketViewProduct {
  id: number
  name: string
  description: string
  product_type: string
  category: string
  image_url: string  // Kept for backward compatibility
  product_url: string
  availability: string
  manufacturing_status: string
  images: ProductImage[]  // New: array of all product images
  primary_image: ProductImage | null  // New: primary image object
}

export interface MarketViewVendor {
  id: number
  name: string
  website: string
  headquarters_location: string
  latitude: number | null
  longitude: number | null
  is_small_business: boolean
  is_8a_certified: boolean
  agency_relationships: AgencyRelationshipSummary[]
}

export interface ScoreDimension {
  /**
   * 0-100. A placeholder unless `status` is 'scored' - read it through
   * `describeScore()` in `@/lib/scoreDisplay` rather than rendering it directly.
   */
  value: number
  label: string
  explanation: string
  details?: Record<string, any>
  /** Optional: absent on payloads cached before DEV-584 shipped. */
  status?: 'scored' | 'not_applicable' | 'not_scored' | 'error'
}

export interface MarketViewScores {
  product_id: number
  vendor_id: number
  /**
   * 0-100 rollup. As real as its inputs - read it through `describeOverall()`
   * in `@/lib/scoreDisplay` rather than rendering it directly.
   */
  overall: number
  /** Optional: absent on payloads cached before DEV-584 shipped. */
  overall_status?: 'scored' | 'not_applicable' | 'not_scored' | 'error'
  need_fit: ScoreDimension
  technical: ScoreDimension
  manufacturing: ScoreDimension
  vc_metrics: ScoreDimension
  dod_partnership: ScoreDimension
}

export interface MarketViewSearchResult {
  product: MarketViewProduct
  vendor: MarketViewVendor
  semantic_score: number
  scores: MarketViewScores
  is_shortlisted?: boolean
}

export interface TechTreeDiscipline {
  id: number
  name: string
  description: string
  technologies: {
    id: number
    name: string
    description: string
    product_count: number
  }[]
}

export interface MarketViewSearchResponse {
  products: MarketViewSearchResult[]
  tech_tree: {
    disciplines: TechTreeDiscipline[]
    total_technologies: number
  }
  total: number
  query: string
  scorecard_id?: number
  scorecard?: any  // Serialized scorecard with sections/items for ScorecardFilterPanel
  // Pagination fields
  search_id?: string
  offset?: number
  page_size?: number
  has_more?: boolean
  tech_focus?: any
}

export interface MarketViewSpec {
  id: number
  name: string
  description: string
  spec_type: string
  data_type: string
  unit: string
  typical_min: number | null
  typical_max: number | null
  categories: string[]
}

export interface MarketViewSpecGroup {
  type: string
  type_display: string
  specs: MarketViewSpec[]
}

export interface MarketViewSpecsResponse {
  spec_groups: MarketViewSpecGroup[]
}

export interface MarketViewRescoreResponse {
  results: {
    product_id: number
    scores: MarketViewScores
  }[]
}

// Recursive technology tree node
export interface TechnologyNode {
  id: number
  name: string
  description: string
  product_count: number
  specs_count: number
  children: TechnologyNode[]
}

export interface MarketViewDiscipline {
  id: number
  name: string
  description: string
  technologies: TechnologyNode[]
}

export interface MarketViewDisciplinesResponse {
  disciplines: MarketViewDiscipline[]
}

export const api = new ApiClient();

